import React, { useState, useRef, useEffect } from "react";
import biologyCalcPool from "./biologyCalcPool";
import { awardToken } from "../../../awardtoken.js";
import "../ChemistryCalculationPracticeTool/ChemistryCalculationPracticeTool.css";

const SHEET_SIZES = [10, 25, 50];

function getRandomProblem(pool) {
  return pool[Math.floor(Math.random() * pool.length)];
}

export default function BiologyCalculationPracticeTool({ onAwardTokens } = {}) {
  // 主題自動整理（type去重）
  const TOPICS = Array.from(new Set(biologyCalcPool.map(q => q.type)));
  const [selectedTopic, setSelectedTopic] = useState("");

  function getFilteredPool() {
    return selectedTopic
      ? biologyCalcPool.filter(q => q.type === selectedTopic)
      : biologyCalcPool;
  }

  const [currentQ, setCurrentQ] = useState(() => getRandomProblem(getFilteredPool()));
  const [userAnswer, setUserAnswer] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [waitingForNext, setWaitingForNext] = useState(false);
  const [sheetProblems, setSheetProblems] = useState(null);
  const [showAnswers, setShowAnswers] = useState(false);
  const tokensAwarded = useRef(0);

// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  const pool = selectedTopic
    ? biologyCalcPool.filter(q => q.type === selectedTopic)
    : biologyCalcPool;
  setCurrentQ(getRandomProblem(pool));
  setUserAnswer("");
  setFeedback(null);
  setAttempts(0);
  setCorrect(0);
  setWaitingForNext(false);
}, [selectedTopic]);

  useEffect(() => {
    if (correct >= (tokensAwarded.current + 1) * 10) {
      awardToken();
      tokensAwarded.current += 1;
      if (onAwardTokens) onAwardTokens(1);
    }
  }, [correct, onAwardTokens]);

  function nextQuestion() {
    setCurrentQ(getRandomProblem(getFilteredPool()));
    setUserAnswer("");
    setFeedback(null);
    setWaitingForNext(false);
  }

  function handleAnswerSubmit(e) {
    e.preventDefault();
    setAttempts(a => a + 1);
    const normalizedUser = userAnswer.trim().toLowerCase();
    const normalizedAns = currentQ.answer.trim().toLowerCase();
    if (normalizedUser === normalizedAns) {
      setCorrect(c => c + 1);
      setFeedback("✅ Correct!");
      setWaitingForNext(true);
    } else {
      setFeedback(`❌ Incorrect. The answer is: ${currentQ.answer} ${currentQ.units}`);
      setWaitingForNext(true);
    }
  }

  function startSheetMode(count) {
    const pool = getFilteredPool();
    const problems = Array.from({ length: count }, () => getRandomProblem(pool));
    setSheetProblems(problems);
    setShowAnswers(false);
    setTimeout(() => window.print(), 100);
  }
  function leaveSheetMode() { setSheetProblems(null); }

  // Worksheet mode
  if (sheetProblems) {
    return (
      <div className="worksheet">
        <h2 className="no-print">Biology Calculation Worksheet</h2>
        <button className="no-print" onClick={leaveSheetMode}>Back to Practice</button>
        <button
          className="no-print"
          style={{ marginLeft: "1em" }}
          onClick={() => setShowAnswers(v => !v)}
        >
          {showAnswers ? "Hide Answers" : "Show Answers"}
        </button>
        <ol>
          {sheetProblems.map((q, idx) => (
            <li key={idx} style={{ marginBottom: "1.1em" }}>
              <div><b>Q{idx + 1}.</b> {q.question}</div>
              <div>Answer: __________ {q.units}</div>
              {showAnswers && (
                <div style={{ color: "#26b973", fontSize: "0.98em" }}>
                  Correct: {q.answer} {q.units}
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>
    );
  }

  // 練習模式
  return (
    <div className="practice-container">
      <h2>Biology Calculation Practice</h2>
      <div style={{ margin: "1em 0" }}>
        <label>
          <strong>Practice Topic:</strong>
          <select
            value={selectedTopic}
            onChange={e => setSelectedTopic(e.target.value)}
            style={{ marginLeft: "1em", minWidth: 140 }}
          >
            <option value="">All</option>
            {TOPICS.map(topic =>
              <option key={topic} value={topic}>
                {topic.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            )}
          </select>
        </label>
      </div>
      <div style={{ margin: "1.3em 0" }}>
        <b>Question:</b> {currentQ.question}
      </div>
      <form onSubmit={handleAnswerSubmit} className="answer-form">
        <input
          type="text"
          value={userAnswer}
          onChange={e => setUserAnswer(e.target.value)}
          autoFocus
          disabled={waitingForNext}
        />
        <span style={{ marginLeft: 10, fontWeight: 500 }}>{currentQ.units}</span>
        <button
          type="submit"
          style={{ marginLeft: "0.7rem" }}
          disabled={waitingForNext}
        >
          Submit
        </button>
      </form>
      {feedback && (
        <div style={{ margin: "1em 0", fontWeight: 500 }}>{feedback}</div>
      )}
      {waitingForNext && (
        <button
          className="math-pill-btn"
          style={{ marginTop: "0.7em" }}
          onClick={nextQuestion}
        >
          Next
        </button>
      )}
      <p>
        Score: {correct} / {attempts}
      </p>
      <div className="worksheet-buttons">
        <strong>Printable worksheets:</strong>
        {SHEET_SIZES.map(cnt => (
          <button key={cnt} onClick={() => startSheetMode(cnt)} style={{ marginLeft: "0.5rem" }}>
            {cnt} Questions
          </button>
        ))}
      </div>
    </div>
  );
}
